import React from 'react';
import { 
  Shield, Eye, Server, Lock, Database, FileText, 
  CheckCircle, Key, Globe, Cpu, Network, AlertTriangle
} from 'lucide-react';

const SecurityPage = () => {
  const principles = [
    {
      icon: Eye,
      title: 'Read-Only by Default',
      description: 'All connectors operate in read-only mode. Agent Control Plane never writes to, modifies, or executes actions in your infrastructure unless explicitly authorized through the Kill Switch with audit-logged approval.',
      color: '#0066FF'
    },
    {
      icon: Server,
      title: 'Customer-Managed Deployment',
      description: 'Deploy entirely within your VPC, on-premise, or air-gapped environment. Your data never leaves your perimeter. We provide the software; you control the infrastructure.',
      color: '#34C759'
    },
    {
      icon: Lock,
      title: 'Least Privilege Architecture',
      description: 'The platform requires minimal permissions to operate. No broad admin access, no standing privileges. Connector credentials are scoped to discovery-only operations.',
      color: '#FFCC00'
    },
    {
      icon: Database,
      title: 'Tenant Isolation',
      description: 'Complete data segregation per customer. Separate encryption keys, isolated compute, and no shared data stores. Cross-tenant access is architecturally impossible.',
      color: '#FF6961'
    },
    {
      icon: Key,
      title: 'Encryption Everywhere',
      description: 'AES-256 at rest, TLS 1.3 in transit. Customer-managed encryption keys (CMEK) supported. All secrets stored in hardware-backed vaults with automatic rotation.',
      color: '#0066FF'
    },
    {
      icon: FileText,
      title: 'Full Auditability',
      description: 'Every action, query, and configuration change is logged immutably. Audit trails are exportable in CEF, STIX, and JSON formats for your SIEM integration.',
      color: '#34C759'
    },
  ];

  const certifications = [
    { name: 'SOC 2 Type II', status: 'In Progress', detail: 'Expected Q3 2026' },
    { name: 'ISO 27001', status: 'Planned', detail: 'Roadmap Q4 2026' },
    { name: 'GDPR Compliant', status: 'Active', detail: 'Data processing agreements available' },
    { name: 'CCPA Compliant', status: 'Active', detail: 'California consumer privacy' },
  ];

  const securityControls = [
    { label: 'Authentication', value: 'JWT + MFA-ready', icon: Shield },
    { label: 'Authorization', value: 'RBAC with least privilege', icon: Lock },
    { label: 'Network', value: 'VPC peering, private endpoints', icon: Network },
    { label: 'Secrets', value: 'Hardware-backed vault, auto-rotation', icon: Key },
    { label: 'Monitoring', value: 'Real-time anomaly detection', icon: AlertTriangle },
    { label: 'Incident Response', value: 'Automated containment + SLA tracking', icon: Cpu },
  ];

  return (
    <div data-testid="security-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Shield className="w-8 h-8 text-[#0066FF]" />
          <h1 className="text-3xl font-bold text-[#F9FAFB]" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Security Architecture
          </h1>
        </div>
        <p className="text-[#9CA3AF]">
          Built for the most security-conscious organizations. Every design decision prioritizes your data sovereignty.
        </p>
      </div>

      {/* Trust Banner */}
      <div className="enterprise-card p-6 mb-8 border-l-4 border-[#0066FF]">
        <div className="flex items-start gap-4">
          <CheckCircle className="w-6 h-6 text-[#34C759] flex-shrink-0 mt-0.5" />
          <div>
            <h2 className="text-lg font-bold text-[#F9FAFB] mb-1">Zero-Trust Security Model</h2>
            <p className="text-[#9CA3AF] text-sm leading-relaxed">
              Agent Control Plane is designed on zero-trust principles. We assume breach, verify continuously,
              and enforce least privilege at every layer. Our platform discovers and governs non-human identities
              without ever requiring write access to your infrastructure.
            </p>
          </div>
        </div>
      </div>

      {/* Security Principles */}
      <div className="mb-8">
        <h2 className="text-sm font-bold text-[#9CA3AF] uppercase tracking-wider mb-4">
          Core Security Principles
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {principles.map((item, i) => (
            <div key={i} className="enterprise-card p-6" data-testid={`security-principle-${i}`}>
              <div
                className="w-10 h-10 flex items-center justify-center mb-4"
                style={{ backgroundColor: `${item.color}15` }}
              >
                <item.icon className="w-5 h-5" style={{ color: item.color }} />
              </div>
              <h3 className="text-base font-bold text-[#F9FAFB] mb-2">{item.title}</h3>
              <p className="text-sm text-[#9CA3AF] leading-relaxed">{item.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Security Controls Summary */}
      <div className="mb-8">
        <h2 className="text-sm font-bold text-[#9CA3AF] uppercase tracking-wider mb-4">
          Security Controls
        </h2>
        <div className="enterprise-card overflow-hidden">
          <table className="data-table" data-testid="security-controls-table">
            <thead>
              <tr className="bg-[#1A1A1A]">
                <th>Control Area</th>
                <th>Implementation</th>
              </tr>
            </thead>
            <tbody>
              {securityControls.map((ctrl, i) => (
                <tr key={i}>
                  <td>
                    <div className="flex items-center gap-3">
                      <ctrl.icon className="w-4 h-4 text-[#0066FF]" />
                      <span className="text-[#F9FAFB]">{ctrl.label}</span>
                    </div>
                  </td>
                  <td className="text-[#9CA3AF]">{ctrl.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Compliance & Certifications */}
      <div className="mb-8">
        <h2 className="text-sm font-bold text-[#9CA3AF] uppercase tracking-wider mb-4">
          Compliance & Certifications
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {certifications.map((cert, i) => (
            <div key={i} className="enterprise-card p-5" data-testid={`cert-${i}`}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-bold text-[#F9FAFB]">{cert.name}</h3>
                <span className={`px-2 py-0.5 text-[10px] font-mono uppercase ${
                  cert.status === 'Active' ? 'text-[#34C759] bg-[#34C759]/10' :
                  cert.status === 'In Progress' ? 'text-[#FFCC00] bg-[#FFCC00]/10' :
                  'text-[#9CA3AF] bg-[#9CA3AF]/10'
                }`}>
                  {cert.status}
                </span>
              </div>
              <p className="text-xs text-[#6B7280]">{cert.detail}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Deployment Security */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="enterprise-card p-6 border-l-4 border-[#34C759]">
          <Globe className="w-6 h-6 text-[#34C759] mb-3" />
          <h3 className="text-base font-bold text-[#F9FAFB] mb-2">SaaS Deployment</h3>
          <p className="text-sm text-[#9CA3AF] mb-3">
            Managed infrastructure with read-only API connectors. Data encrypted at rest with customer-specific keys.
          </p>
          <ul className="space-y-1 text-xs text-[#6B7280]">
            <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-[#34C759]" /> SOC 2 environment</li>
            <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-[#34C759]" /> 99.9% SLA</li>
            <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-[#34C759]" /> Automated backups</li>
          </ul>
        </div>
        <div className="enterprise-card p-6 border-l-4 border-[#0066FF]">
          <Server className="w-6 h-6 text-[#0066FF] mb-3" />
          <h3 className="text-base font-bold text-[#F9FAFB] mb-2">Customer VPC</h3>
          <p className="text-sm text-[#9CA3AF] mb-3">
            Full deployment in your cloud environment. No data leaves your perimeter. You manage the keys.
          </p>
          <ul className="space-y-1 text-xs text-[#6B7280]">
            <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-[#34C759]" /> Your infrastructure</li>
            <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-[#34C759]" /> CMEK support</li>
            <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-[#34C759]" /> Private networking</li>
          </ul>
        </div>
        <div className="enterprise-card p-6 border-l-4 border-[#FFCC00]">
          <Lock className="w-6 h-6 text-[#FFCC00] mb-3" />
          <h3 className="text-base font-bold text-[#F9FAFB] mb-2">Air-Gapped</h3>
          <p className="text-sm text-[#9CA3AF] mb-3">
            Complete isolation. No external connections. On-premise deployment with offline updates.
          </p>
          <ul className="space-y-1 text-xs text-[#6B7280]">
            <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-[#34C759]" /> Zero egress</li>
            <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-[#34C759]" /> Offline operation</li>
            <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-[#34C759]" /> Physical security ready</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SecurityPage;
